import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AnalysisManagementService } from '../../services/analysis-management.service';
import { NavBarComponent } from '../../nav-bar/nav-bar.component';
import { AvailableMapsFieldModel } from '../../models/availableMapsFieldModel';
import { MapsTypesAvailabilityModel } from '../../models/mapsTypesAvailabilityModel';
import { FieldAvailableMapsDto } from '../../dto/FieldAvailableMapsDto';
import dayjs from 'dayjs';

@Component({
  selector: 'app-custom-sequence-selection',
  standalone: false,
  templateUrl: './custom-sequence-selection.component.html',
  styleUrl: './custom-sequence-selection.component.css',
})
export class CustomSequenceSelectionComponent implements OnInit {

  analysisTypes: MapsTypesAvailabilityModel[] = [];
  filteredList: AvailableMapsFieldModel[] = [];
  selectedItems: AvailableMapsFieldModel[] = [];

  selectedType: MapsTypesAvailabilityModel | null = null;
  selectedPeriod: string = '30 days';
  showNoDataCard: boolean = false;
  hideLoadMoreBtn: boolean = false;
  showChemList: boolean = false;
  showAnalysisList: boolean = false;
  disablePeriodRange: boolean = false;

  private filteredItemObj = {
    period: '30 days',
    fieldId: 0,
    type: '',
    pageSize: 25,
    pageNumber: 1,
  };

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: { fieldId: number; analysisTypes: MapsTypesAvailabilityModel[]; previousSelectedItems?: AvailableMapsFieldModel[] },
    private dialogRef: MatDialogRef<CustomSequenceSelectionComponent>,
    private analysisManagementService: AnalysisManagementService,
    public nav: NavBarComponent,
  ) {}

  ngOnInit(): void {
    this.analysisTypes = (this.data.analysisTypes || []).map(t => ({ ...t, selected: false }));
    this.selectedItems = this.data.previousSelectedItems ? [...this.data.previousSelectedItems] : [];
    this.filteredItemObj.fieldId = this.data.fieldId;

    if (this.analysisTypes.length > 0) {
      this.selectType(this.analysisTypes[0]);
    }
  }

  selectType(type: MapsTypesAvailabilityModel): void {
    if (!type.available) return;

    this.analysisTypes.forEach(t => t.selected = t.type === type.type && t.alias === type.alias);
    this.selectedType = type;
    this.filteredItemObj.type = type.type;
    this.filteredItemObj.pageNumber = 1;
    this.filteredList = [];

    const isSatellite = type.type.toLowerCase().includes('satellite');
    const isTopography = type.type.toLowerCase().includes('topography');

    if (isSatellite && !isTopography) {
      this.showAnalysisList = true;
      this.showChemList = false;
      this.disablePeriodRange = false;
      this.filteredItemObj.period = this.selectedPeriod;
    } else if (isTopography) {
      this.showAnalysisList = true;
      this.showChemList = false;
      this.disablePeriodRange = true;
      this.filteredItemObj.period = 'all';
    } else {
      this.showAnalysisList = false;
      this.showChemList = true;
      this.disablePeriodRange = true;
      this.filteredItemObj.period = 'all';
    }

    this.hideLoadMoreBtn = this.filteredItemObj.period === 'all';
    this.getFilteredList();
  }

  clickPeriod(period: string): void {
    this.selectedPeriod = period;
    this.filteredItemObj.period = period;
    this.filteredItemObj.pageNumber = 1;
    this.filteredList = [];
    this.hideLoadMoreBtn = period === 'all';
    this.getFilteredList();
  }

  getFilteredList(): void {
    this.nav.toggleLoading(true);
    const dto: FieldAvailableMapsDto = {
      period: this.filteredItemObj.period,
      fieldId: this.filteredItemObj.fieldId,
      type: this.filteredItemObj.type,
      pageSize: this.filteredItemObj.pageSize,
      pageNumber: this.filteredItemObj.pageNumber,
    };

    this.analysisManagementService.getAvailableMapsField(dto).subscribe({
      next: (items) => {
        if (this.filteredItemObj.pageNumber <= 1) {
          this.filteredList = items;
        } else {
          items.forEach(i => this.filteredList.push(i));
        }
        this.showNoDataCard = items.length > 0;
      },
      error: () => {},
      complete: () => this.nav.toggleLoading(false),
    });
  }

  loadMore(): void {
    this.filteredItemObj.pageNumber++;
    this.getFilteredList();
  }

  isSelected(item: AvailableMapsFieldModel): boolean {
    return this.selectedItems.some(s => s.analysisId === item.analysisId && s.type === item.type);
  }

  toggleItem(item: AvailableMapsFieldModel): void {
    const idx = this.selectedItems.findIndex(s => s.analysisId === item.analysisId && s.type === item.type);
    if (idx > -1) {
      this.selectedItems.splice(idx, 1);
    } else {
      this.selectedItems.push(item);
    }
  }

  isChemItemSelected(parent: AvailableMapsFieldModel, itemIndex: number): boolean {
    return this.selectedItems.some(
      s => s.analysisId === parent.analysisId && s.items[0]?.propertyTypeId === parent.items[itemIndex].propertyTypeId
    );
  }

  toggleChemItem(parent: AvailableMapsFieldModel, itemIndex: number): void {
    const syntheticItem: AvailableMapsFieldModel = {
      ...parent,
      items: [parent.items[itemIndex]],
    };
    const idx = this.selectedItems.findIndex(
      s => s.analysisId === parent.analysisId && s.items[0]?.propertyTypeId === parent.items[itemIndex].propertyTypeId
    );
    if (idx > -1) {
      this.selectedItems.splice(idx, 1);
    } else {
      this.selectedItems.push(syntheticItem);
    }
  }

  removeSelected(item: AvailableMapsFieldModel): void {
    const idx = this.selectedItems.findIndex(
      s => s.analysisId === item.analysisId && s.items[0]?.propertyTypeId === item.items[0]?.propertyTypeId
    );
    if (idx > -1) {
      this.selectedItems.splice(idx, 1);
    }
  }

  clearAll(): void {
    this.selectedItems = [];
  }

  formatDate(date: string, format: string): string {
    return dayjs(date).format(format);
  }

  confirm(): void {
    this.dialogRef.close({ selectedItems: this.selectedItems });
  }

  close(): void {
    this.dialogRef.close();
  }
}
